import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import { css } from '@emotion/react'
import React, { type ComponentProps, type Ref } from 'react'

import { UnstyledAnchor } from '../../components/UnstyledAnchor'
import { UnstyledButton } from '../../components/UnstyledButton'
import {
  ExtendedPaletteColorName,
  getLightThemeExtendedColorComponents,
} from '../../shared/MobiusColors'
import type { ReactAnchorProps, ReactButtonProps } from '../../types'
import {
  CssVars,
  NeutralColorToken,
  type ProductTheme,
  SemanticColorToken,
  type ThemeColorToken,
} from '../themes'
import { FoundationIcon } from './FoundationIcon'
import type { Icon16 } from './FoundationIcon/icons/directory'
import { HoverState } from './HoverState'
import { InternalImage } from './internal/InternalImage'
import { NumberPog } from './NumberPog'
import { Spinner16 } from './Spinner'
import { TextColorOverride } from './TextColorOverride'
import { getColorValue, useTheme } from './ThemeProvider'
import { Type } from './Type'

export type ChipAccessorySide = 'left' | 'right'

type ChipCountAccessory = {
  count: number
  icon?: never
  numberPogColorOverrides?: {
    foregroundColorOverride?: ComponentProps<
      typeof NumberPog
    >['foregroundColorOverride']
    backgroundColorOverride?: ComponentProps<
      typeof NumberPog
    >['backgroundColorOverride']
  }
  thumbnailAltText?: never
  thumbnailUrl?: never
  side?: never
}

type ChipThumbnailAccessory = {
  count?: never
  icon?: never
  numberPogColorOverrides?: never
  thumbnailAltText: string
  thumbnailUrl: string
  side?: never
}

type ChipIconAccessory = {
  count?: never
  icon: Icon16
  numberPogColorOverrides?: never
  thumbnailAltText?: never
  thumbnailUrl?: never
  /**
   * Default side for icon is left
   */
  side?: ChipAccessorySide
}

type PresentationProps = { innerRef?: Ref<HTMLDivElement> }

// Note (emmad.usmani): refs are currently not supported on the 'anchor' type,
// because the underlying LinkComponent used for 'anchor' does not forward a ref
type AnchorProps = Omit<ReactAnchorProps, 'ref' | 'children'>

type ButtonProps = { innerRef?: Ref<HTMLButtonElement> } & Omit<
  ReactButtonProps,
  'ref' | 'children'
>

type ChipState = 'default' | 'disabled' | 'loading' | 'highlighted'

export type ChipProps = {
  /**
   * Optional attachment for chip, could be either a counter using NumberPog, a thumbnail image, or an icon.
   */
  accessory?: ChipCountAccessory | ChipThumbnailAccessory | ChipIconAccessory
  /**
   * Label that will be displayed for the chip.
   */
  label: string
  overrideRootStyles?: CSSInterpolation
  /**
   * Optional state of chip component, note that for loading state we will show a spinner and hide the accessory.
   */
  state?: ChipState
} & (
  | ({ type: 'presentation' } & PresentationProps)
  | ({ type: 'anchor' } & AnchorProps)
  | ({ type: 'button' } & ButtonProps)
) &
  (
    | {
        /**
         * Optional prop to indicate whether chip is selected (used for filter lists). If you use this, special ghost coloring will be applied and custom colors are not allowed.
         */
        selected?: boolean
        backgroundColor?: never
        foregroundColor?: never
        borderColor?: never
      }
    | {
        selected?: never
        /**
         * The background color for the Chip. Note that custom colors are incompatible with the `selected` prop.
         */
        backgroundColor?: SemanticColorToken | ThemeColorToken
        /**
         * The foreground color for the Chip, used for the text and and any accessory. Note that custom colors are incompatible with the `selected` prop.
         */
        foregroundColor?: SemanticColorToken | ThemeColorToken
        /**
         * The border color for the Chip. Note that custom colors are incompatible with the `selected` prop.
         */
        borderColor?: SemanticColorToken | ThemeColorToken
      }
  )

const baseAccessoryCss = {
  display: 'flex',
  margin: '-2px 8px',
}

export function Chip({
  accessory,
  backgroundColor: backgroundColorProp,
  borderColor: borderColorProp,
  foregroundColor: foregroundColorProp,
  label,
  overrideRootStyles,
  selected,
  state,
  type,
  ...rest
}: ChipProps) {
  const theme = useTheme()

  const selectedColorComponents = getLightThemeExtendedColorComponents(
    ExtendedPaletteColorName.Prime
  )

  const isDisabled = state === 'disabled'
  // Only apply hover/active styles if computed component state is interactable
  const isInteractable = type !== 'presentation' && !isDisabled

  const backgroundColor = getBackgroundColor(
    backgroundColorProp,
    isDisabled,
    selected
  )

  let accessorySide: ChipAccessorySide
  let accessoryComponent: React.ReactNode
  if (accessory) {
    accessorySide =
      accessory.side ??
      (accessory.icon || accessory.thumbnailUrl ? 'left' : 'right')

    if (accessory?.icon !== undefined) {
      accessoryComponent = (
        <IconAccessory accessory={accessory} side={accessorySide} />
      )
    } else if (accessory?.thumbnailUrl !== undefined) {
      accessoryComponent = (
        <ThumbnailAccessory
          accessory={accessory}
          side={accessorySide}
          state={state}
        />
      )
    } else if (accessory?.count !== undefined) {
      accessoryComponent = (
        <CountAccessory
          accessory={accessory}
          selected={selected}
          side={accessorySide}
          state={state}
        />
      )
    }
  } else {
    accessoryComponent = null
  }

  const content = (
    <TextColorOverride
      color={getForegroundColor(foregroundColorProp, isDisabled, selected)}
    >
      {({ rootClassName: textColorClassName }) => (
        <div
          className={textColorClassName}
          css={{
            alignItems: 'center',
            display: 'flex',
          }}
        >
          {accessorySide === 'left' &&
            state !== 'loading' &&
            accessoryComponent}
          {state === 'loading' && (
            <Spinner16
              color={NeutralColorToken.Fg1}
              overrideRootStyles={{
                left: '50%',
                position: 'absolute',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}
          <Type.Text
            overrideRootStyles={
              state === 'loading' && {
                userSelect: 'none',
                visibility: 'hidden',
              }
            }
            size="S"
          >
            {label}
          </Type.Text>
          {accessorySide === 'right' &&
            state !== 'loading' &&
            accessoryComponent}
        </div>
      )}
    </TextColorOverride>
  )

  return (
    <HoverState color={backgroundColor} isActive={state === 'highlighted'}>
      {({ colorVariable, hoverableClassName, rootClassName }) => {
        const renderProps = {
          'aria-busy': state === 'loading',
          children: content,
          className: cx(rootClassName, hoverableClassName),
          css: css([
            {
              alignItems: 'center',
              backgroundColor:
                isInteractable || state === 'highlighted'
                  ? colorVariable
                  : getColorValue(backgroundColor, theme),
              border: `1px solid ${getBorderColorValue(
                borderColorProp,
                isDisabled,
                selected,
                theme
              )}`,
              borderRadius: '40px',
              cursor:
                (state === 'disabled' && 'not-allowed') ||
                ((type === 'button' || type === 'anchor') && 'pointer') ||
                'default',
              display: 'inline-flex',
              padding: `8px 16px`,
              position: 'relative',
              // Note (@auster.chen): For selected state we apply a background with opacity
              // This isn't supported by `HoverState` so we override this
            },
            selected && [
              {
                backgroundColor: `rgba(
                  ${selectedColorComponents.r},
                  ${selectedColorComponents.g},
                  ${selectedColorComponents.b},
                  ${state === 'highlighted' ? '0.2' : '0.1'}
                )`,
              },
              // NOTE(trevor.healy): Order of the pseudo classes matter, and key sorting will try to put :active before :hover
              isInteractable && {
                '&:hover': {
                  backgroundColor: `rgba(${selectedColorComponents.r}, ${selectedColorComponents.g}, ${selectedColorComponents.b}, 0.2)`,
                },
              },
              isInteractable && {
                '&:active': {
                  backgroundColor: `rgba(${selectedColorComponents.r}, ${selectedColorComponents.g}, ${selectedColorComponents.b}, 0.3)`,
                },
              },
            ],
            overrideRootStyles,
          ]),
        }

        if (
          type === 'presentation' ||
          // Note (@auster.chen): anchor doesn't have a disabled state so it becomes a presentation
          (type === 'anchor' && state === 'disabled')
        ) {
          return (
            <div {...renderProps} ref={(rest as PresentationProps).innerRef} />
          )
        }

        if (type === 'button') {
          const { innerRef, ...otherProps } = rest as ButtonProps
          return (
            <UnstyledButton
              {...renderProps}
              ref={innerRef}
              disabled={state === 'disabled'}
              {...otherProps}
            />
          )
        }

        if (type === 'anchor') {
          return <UnstyledAnchor {...renderProps} {...(rest as AnchorProps)} />
        }

        return null
      }}
    </HoverState>
  )
}

type IconAccessoryProps = {
  accessory: ChipIconAccessory
  side: 'left' | 'right'
}

function IconAccessory({ accessory, side }: IconAccessoryProps) {
  return (
    <div
      // TODO (@auster.chen): https://robinhood.atlassian.net/browse/DS-2169
      aria-hidden
      css={[
        baseAccessoryCss,
        side === 'left'
          ? {
              marginLeft: 0,
            }
          : {
              marginRight: 0,
            },
      ]}
    >
      <FoundationIcon type={accessory.icon} />
    </div>
  )
}

type ThumbnailAccessoryProps = {
  accessory: ChipThumbnailAccessory
  side: 'left' | 'right'
  state?: ChipState
}

function ThumbnailAccessory({
  accessory,
  side,
  state,
}: ThumbnailAccessoryProps) {
  return (
    <div
      css={[
        baseAccessoryCss,
        {
          opacity: state === 'disabled' ? 0.4 : 1,
        },
        side === 'left'
          ? {
              marginLeft: -10,
            }
          : {
              marginRight: -10,
            },
      ]}
    >
      <InternalImage
        alt={accessory?.thumbnailAltText}
        css={{
          borderRadius: '50%',
        }}
        height="24px"
        src={accessory?.thumbnailUrl}
        width="24px"
      />
    </div>
  )
}

type CountAccessoryProps = {
  accessory: ChipCountAccessory
  side: 'left' | 'right'
  state?: ChipState
  selected?: boolean
}

function CountAccessory({
  accessory,
  selected,
  side,
  state,
}: CountAccessoryProps) {
  return (
    <div
      css={[
        baseAccessoryCss,
        side === 'left' ? { marginLeft: -10 } : { marginRight: -10 },
      ]}
    >
      <NumberPog
        backgroundColorOverride={
          selected ? SemanticColorToken.Positive : NeutralColorToken.Bg3
        }
        foregroundColorOverride={
          selected ? NeutralColorToken.Bg1 : NeutralColorToken.Fg1
        }
        isDisabled={state === 'disabled'}
        number={accessory?.count}
        {...accessory?.numberPogColorOverrides}
      />
    </div>
  )
}

function getBackgroundColor(
  backgroundColorProp: SemanticColorToken | ThemeColorToken | undefined,
  isDisabled: boolean,
  selected: boolean | undefined
) {
  if (isDisabled) return NeutralColorToken.Bg2

  if (selected !== undefined) return NeutralColorToken.Bg1

  return backgroundColorProp ?? NeutralColorToken.Bg1
}

function getForegroundColor(
  foregroundColorProp: SemanticColorToken | ThemeColorToken | undefined,
  isDisabled: boolean,
  selected: boolean | undefined
) {
  if (isDisabled) return NeutralColorToken.Fg3

  if (selected === undefined && foregroundColorProp) return foregroundColorProp

  return selected ? SemanticColorToken.Positive : NeutralColorToken.Fg1
}

function getBorderColorValue(
  borderColorProp: SemanticColorToken | ThemeColorToken | undefined,
  isDisabled: boolean,
  selected: boolean | undefined,
  theme: ProductTheme
) {
  const borderColorPropValue = borderColorProp
    ? getColorValue(borderColorProp, theme)
    : undefined

  if (isDisabled) return CssVars.neutral.bg3

  if (selected === undefined && borderColorPropValue)
    return borderColorPropValue

  return selected ? CssVars.semantic.positive.base : CssVars.neutral.bg3
}
